import Image from "next/image";
import { Repeat2, ThumbsUp, ExternalLink, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { PreviewModal } from "./preview-modal";

const CARD_GRADIENTS = [
  "bg-linear-to-br from-[#ff7a45] via-[#ff4d4d] to-[#c63838]",
  "bg-linear-to-br from-[#a855f7] via-[#9333ea] to-[#6d28d9]",
  "bg-linear-to-br from-[#475569] via-[#334155] to-[#1e293b]",
  "bg-linear-to-br from-[#5eead4] via-[#14b8a6] to-[#0d9488]",
  "bg-linear-to-br from-[#3b82f6] via-[#6366f1] to-[#4338ca]",
  "bg-linear-to-br from-[#fb7185] via-[#fb923c] to-[#fbbf24]",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return CARD_GRADIENTS[Math.abs(hash) % CARD_GRADIENTS.length];
}

interface CommunityTemplateCardProps {
  projectId: string;
  title: string;
  authorName: string;
  views: number;
  likes: number;
  hasLiked?: boolean;
  remixCount?: number;
  screenshotSrc?: string;
  avatarSrc?: string;
  avatarEmoji?: string;
  avatarColor?: string;
  avatarType?: string;
  vercelUrl?: string | null;
  isRemixing?: boolean;
  showBadge?: boolean;
  onRemix?: (projectId: string) => void;
  onLikeChange?: (projectId: string, newLikeCount: number) => void;
  onLoginRequired?: () => void;
}

export function CommunityTemplateCard({
  projectId,
  title,
  authorName,
  likes,
  hasLiked: hasLikedProp,
  remixCount = 0,
  screenshotSrc = "/community/neo-brutalist-card.png",
  avatarSrc,
  avatarEmoji,
  avatarColor,
  avatarType,
  vercelUrl,
  isRemixing = false,
  showBadge = false,
  onRemix,
  onLikeChange,
  onLoginRequired,
}: CommunityTemplateCardProps) {
  const [likeCount, setLikeCount] = useState(likes);
  const [hasLiked, setHasLiked] = useState(Boolean(hasLikedProp));
  const [isLiking, setIsLiking] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);

  useEffect(() => {
    setLikeCount(likes);
    setHasLiked(Boolean(hasLikedProp));
  }, [likes, hasLikedProp]);

  const handleLike = async () => {
    if (isLiking) return;

    setIsLiking(true);
    try {
      const response = await fetch(`/api/projects/${projectId}/like`, {
        method: hasLiked ? "DELETE" : "POST",
      });

      if (response.ok) {
        const data = await response.json();
        setLikeCount(data.likeCount);
        setHasLiked(!hasLiked);
        onLikeChange?.(projectId, data.likeCount);
      } else if (response.status === 401) {
        toast.info("Sign in to like projects");
        onLoginRequired?.();
      } else if (response.status === 409) {
        setHasLiked(true);
      } else {
        toast.error("Couldn't update like. Please try again.");
      }
    } catch {
      toast.error("Couldn't update like. Please try again.");
    } finally {
      setIsLiking(false);
    }
  };

  const handlePreview = () => {
    // Open the preview modal
    setIsPreviewModalOpen(true);
  };
  const gradientClass = pickGradient(projectId);

  return (
    <>
      <div className="group min-w-0 w-full overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg sm:rounded-3xl">
        {/* Gradient screenshot frame */}
        <button
          type="button"
          onClick={handlePreview}
          className={`relative block w-full ${gradientClass} p-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2`}
          title="Preview project"
          aria-label={`Preview ${title}`}
        >
          <div className="relative overflow-hidden rounded-2xl shadow-md">
            <Image
              src={screenshotSrc}
              alt={`${title} website preview`}
              width={600}
              height={360}
              className="block aspect-5/3 w-full object-cover object-top"
              loading="lazy"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
              unoptimized
            />
          </div>

          {showBadge && (
            <span
              onClick={(e) => {
                e.stopPropagation();
                window.open(
                  `/api/badge-click?project_id=${encodeURIComponent(projectId)}&ref=${encodeURIComponent(projectId)}`,
                  "_blank",
                  "noopener,noreferrer",
                );
              }}
              className="absolute right-6 top-6 z-10 inline-flex h-9 flex-nowrap items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#0A0A0A] px-3 shadow-[0_3px_15px_0_rgba(0,0,0,0.28)] font-[family-name:var(--font-rubik),sans-serif]"
            >
              <span className="shrink-0 text-[12px] font-normal leading-4 text-[#9CA3AF]">
                Remix with
              </span>
              <img
                src="/panda-lockup.png"
                alt=""
                aria-hidden
                style={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  objectFit: "cover",
                  objectPosition: "left",
                  display: "block",
                  flexShrink: 0,
                }}
              />
              <span className="shrink-0 text-[12px] font-medium leading-4 text-[#D4D4D8]">
                Panda
              </span>
            </span>
          )}

          <span className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-0 transition-opacity duration-200 group-hover:opacity-100">
            <span className="inline-flex items-center gap-2 rounded-lg bg-white/95 px-3 py-2 text-xs font-medium text-zinc-900 shadow-md ring-1 ring-black/5">
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </span>
          </span>
        </button>

        {/* White bottom: title + meta */}
        <div className="px-4 py-3 sm:px-6 sm:py-5">
          <h3
            className="truncate font-[family-name:var(--font-rubik),sans-serif] text-[16px] font-medium leading-6 tracking-normal text-foreground sm:leading-7"
            title={title}
          >
            {title}
          </h3>

          <div className="mt-2 flex items-center justify-between gap-3 sm:mt-4">
            <div className="flex min-w-0 items-center gap-2">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted sm:h-9 sm:w-9">
                {avatarType === "default" && avatarEmoji ? (
                  <div
                    className={`flex h-full w-full items-center justify-center rounded-full bg-linear-to-br ${avatarColor} text-lg shadow-sm`}
                  >
                    {avatarEmoji}
                  </div>
                ) : avatarSrc ? (
                  <Image
                    src={avatarSrc}
                    alt={authorName}
                    width={36}
                    height={36}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    unoptimized
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-full bg-linear-to-br from-gray-200 to-gray-300 text-lg shadow-sm">
                    🐼
                  </div>
                )}
              </div>
              <span
                className="truncate font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-[#77869D]"
                title={authorName}
              >
                {authorName}
              </span>
            </div>

            {/* Like — secondary action / social proof. The remix count moves
                next to the primary "Clone this" button below. */}
            <div className="flex shrink-0 items-center gap-4 font-[family-name:var(--font-rubik),sans-serif] text-sm font-normal leading-5 tracking-normal text-muted-foreground">
              <span
                className="flex items-center gap-1.5"
                title={`${remixCount} clones`}
              >
                <Repeat2 className="h-5 w-5 sm:h-4 sm:w-4" strokeWidth={2.2} aria-hidden />
                <span className="font-normal text-foreground">{remixCount}</span>
              </span>

              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className={`flex h-auto items-center gap-1.5 rounded-full p-0 transition-colors ${
                  hasLiked
                    ? "text-orange-500 hover:text-orange-600"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={handleLike}
                disabled={isLiking}
                title={hasLiked ? "Unlike this project" : "Like this project"}
              >
                <ThumbsUp
                  className={`h-5 w-5 sm:h-4 sm:w-4 ${hasLiked ? "fill-current" : ""}`}
                  strokeWidth={2.2}
                />
                <span className="font-normal text-foreground">{likeCount}</span>
              </Button>
            </div>
          </div>

          {/* PRIMARY action: clone (fork) this community build into your own
              project to start building. Promoted from the old small Remix icon
              so the gallery is build-first, not browse-only (homepage ticket).
              Preview stays available via the screenshot above. */}
          <button
            type="button"
            onClick={() => onRemix?.(projectId)}
            disabled={isRemixing}
            aria-busy={isRemixing}
            title={isRemixing ? "Cloning…" : "Clone this project"}
            className="mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-linear-to-r from-[#FF5606] via-[#D52A26] to-[#AA0346] font-[family-name:var(--font-rubik),sans-serif] text-sm font-semibold text-white shadow-md shadow-orange-500/25 transition-all hover:-translate-y-px hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400 focus-visible:ring-offset-2"
          >
            {isRemixing ? (
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.2} />
            ) : (
              <Repeat2 className="h-4 w-4" strokeWidth={2.2} />
            )}
            {isRemixing ? "Cloning…" : "Clone this"}
          </button>
        </div>
      </div>

      <PreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        projectId={projectId}
        title={title}
        vercelUrl={vercelUrl}
        screenshotSrc={screenshotSrc}
        likes={likeCount}
        hasLiked={hasLiked}
        remixCount={remixCount}
        isRemixing={isRemixing}
        onRemix={onRemix}
        onLikeToggle={handleLike}
      />
    </>
  );
}
